import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { Theme } from '@mui/material';
import {
  lightBlueIndigo,
  darkBlack,
  darkBlue,
  lightAmber,
  lightBlue,
  lightBrown,
  lightGray,
  lightGreen,
  lightLime,
  lightOrange,
  lightPink,
  lightPurple,
  lightRed,
  lightTeal,
  lightYellow,
} from './themeProvider';


/**
 * Minimal root state shape required by this slice.
 * Consumer apps must include both Theme and User (with uid) in their store.
 */
export interface ThemeRootState {
  User: {
    user: {
      uid: string;
    };
  };
  Theme: ThemeState;
}

const THEME_URL = 'https://agent-api.trii.app/api/v1/settings/user/setTheme';

/**
 * Theme name type - defines all available theme options
 */
export type ThemeName =
  | 'lightBlueIndigo'
  | 'darkBlack'
  | 'darkBlue'
  | 'lightAmber'
  | 'lightBlue'
  | 'lightBrown'
  | 'lightGray'
  | 'lightGreen'
  | 'lightLime'
  | 'lightOrange'
  | 'lightPink'
  | 'lightPurple'
  | 'lightRed'
  | 'lightTeal'
  | 'lightYellow';

/**
 * Theme slice state interface
 */
export interface ThemeState {
  themeSelected: Theme;
  darkMode: boolean;
  toggleStatus: 'idle' | 'toggled';
}

const initialState: ThemeState = {
  themeSelected: lightBlueIndigo,
  darkMode: false,
  toggleStatus: 'idle',
};

/**
 * Async thunk to update theme on the server
 */
export const updateTheme = createAsyncThunk<
  { theme: ThemeName },
  ThemeName,
  { state: ThemeRootState }
>('Theme/updateTheme', async (theme, { getState }) => {
  const { uid } = getState().User.user;
  const themeJson = JSON.stringify({ theme, uid });

  const response = await axios.put<{ theme: ThemeName }>(THEME_URL, themeJson);
  return response.data;
});

/**
 * Theme slice
 */
const themeSlice = createSlice({
  name: 'Theme',
  initialState,
  reducers: {
    /**
     * Toggle theme by name
     */
    toggleTheme: (state, action: PayloadAction<ThemeName>): void => {
      const theme = getTheme(action.payload);
      Object.assign(state, { themeSelected: theme, toggleStatus: 'toggled' });
    },
    /**
     * Toggle dark mode
     */
    toggleModeTheme: (state): void => {
      state.darkMode = !state.darkMode;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateTheme.fulfilled, (state, action): void => {
      const theme = getTheme(action.payload.theme);
      Object.assign(state, { themeSelected: theme });
    });
  },
});

/**
 * Get theme object by name
 * @param theme - Theme name identifier
 * @returns Material UI Theme object
 */
const getTheme = (theme: ThemeName): Theme => {
  switch (theme) {
    case 'lightBlueIndigo':
      return lightBlueIndigo;
    case 'darkBlack':
      return darkBlack;
    case 'darkBlue':
      return darkBlue;
    case 'lightAmber':
      return lightAmber;
    case 'lightBlue':
      return lightBlue;
    case 'lightBrown':
      return lightBrown;
    case 'lightGray':
      return lightGray;
    case 'lightGreen':
      return lightGreen;
    case 'lightLime':
      return lightLime;
    case 'lightOrange':
      return lightOrange;
    case 'lightPink':
      return lightPink;
    case 'lightPurple':
      return lightPurple;
    case 'lightRed':
      return lightRed;
    case 'lightTeal':
      return lightTeal;
    case 'lightYellow':
      return lightYellow;
    default:
      return lightBlueIndigo;
  }
};

/**
 * Type guard to check if a string is a valid ThemeName
 * @param theme - String to validate
 * @returns true if theme is a valid ThemeName
 */
export const isValidThemeName = (theme: string): theme is ThemeName => {
  const validThemes: ThemeName[] = [
    'lightBlueIndigo',
    'darkBlack',
    'darkBlue',
    'lightAmber',
    'lightBlue',
    'lightBrown',
    'lightGray',
    'lightGreen',
    'lightLime',
    'lightOrange',
    'lightPink',
    'lightPurple',
    'lightRed',
    'lightTeal',
    'lightYellow',
  ];
  return validThemes.includes(theme as ThemeName);
};

export const { toggleTheme, toggleModeTheme } = themeSlice.actions;

// Typed selectors
export const selectThemeToggleStatus = (state: ThemeRootState) =>
  state.Theme.toggleStatus;
export const selectedTheme = (state: ThemeRootState) => state.Theme.themeSelected;
export const selectedDarkTheme = (state: ThemeRootState) => state.Theme.darkMode;

export default themeSlice.reducer;
